import React, { useState, useRef } from "react";
import {
  Box,
  TextField,
  IconButton,
  Tooltip,
  Fade,
  useMediaQuery,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import AddIcon from "@mui/icons-material/Add";
import StopRoundedIcon from "@mui/icons-material/StopRounded";
import {
  MAX_FILE_SIZE_MB,
  SUPPORTED_TYPES,
} from "../../../core/constants/validateFile";
import {
  composerBottomPadSx,
  mobileNavMediaQuery,
} from "../../../layout/layoutMetrics";

export default function ChatInput({
  onSend,
  onUpload,
  onFocus,
  loading = false,
  isResponding = false,
  onStop,
}) {
  const [value, setValue] = useState("");
  const [fileError, setFileError] = useState("");
  const inputRef = useRef();
  const isMobileNav = useMediaQuery(mobileNavMediaQuery);

  const submit = () => {
    if (!value.trim()) return;
    onSend?.(value.trim());
    setValue("");
  };

  const handleFiles = (files) => {
    const file = files[0];
    if (!file) return;

    setFileError("");
    const ext = `.${file.name.split(".").pop().toLowerCase()}`;
    if (!SUPPORTED_TYPES.includes(ext)) {
      setFileError("Unsupported file type. Only CSV, XLSX, Parquet allowed.");
      return;
    }

    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      setFileError(
        `File too large. Maximum allowed size is ${MAX_FILE_SIZE_MB} MB.`,
      );
      return;
    }

    onFocus?.();
    try {
      onUpload?.([], file.name, file);
      inputRef.current.value = "";
    } catch {
      setFileError("Failed to parse file.");
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        mx: "auto",
        mb: 2,
        [`@media ${mobileNavMediaQuery}`]: {
          ...composerBottomPadSx,
          mb: 0,
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          boxSizing: "border-box",
        },
      }}
    >
      <input
        ref={inputRef}
        type="file"
        style={{ display: "none" }}
        accept={SUPPORTED_TYPES.join(",")}
        onChange={(e) => handleFiles(e.target.files)}
      />

      <Tooltip title={fileError || "Upload CSV, XLSX or Parquet"}>
        <IconButton
          onClick={() => {
            onFocus?.();
            inputRef.current.click();
          }}
          sx={{
            color: "rgba(255,255,255,0.7)",
            backgroundColor: "rgba(255,255,255,0.2)",
          }}
        >
          <AddIcon />
        </IconButton>
      </Tooltip>

      <Box
        sx={{
          flexGrow: 1,
          display: "flex",
          alignItems: "center",
          border: "1px solid rgba(255,255,255,0.15)",
          borderRadius: "24px",
          px: 2,
          py: 0.5,
          backdropFilter: "blur(8px)",
          WebkitBackdropFilter: "blur(8px)",
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          [`@media ${mobileNavMediaQuery}`]: {
            minWidth: 0,
            px: 1.25,
            boxSizing: "border-box",
          },
        }}
      >
        <TextField
          fullWidth
          multiline
          maxRows={6}
          variant="standard"
          value={value}
          disabled={loading && !isResponding}
          placeholder="Upload a file & ask: show table, visualize column..."
          autoComplete="off"
          inputProps={{
            autoComplete: "off",
            "data-1p-ignore": true,
            "data-lpignore": "true",
          }}
          onChange={(e) => setValue(e.target.value)}
          onFocus={(e) => {
            onFocus?.();
            // Mobile only: keep composer above the keyboard without centering
            // (block:"center" scrolls the page up and clips ModeButtons).
            if (!isMobileNav) return;
            requestAnimationFrame(() => {
              e.target?.scrollIntoView?.({
                block: "nearest",
                behavior: "smooth",
              });
            });
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              if (isResponding) {
                onStop?.();
                return;
              }
              submit();
            }
          }}
          InputProps={{ disableUnderline: true }}
          sx={{
            minWidth: 0,
            "& textarea": {
              padding: "10px 0px",
              color: "white",
              fontSize: { xs: 14, sm: 14 },
              wordBreak: "break-word",
            },
          }}
        />
        <Tooltip
          title={isResponding ? "Stop Streaming" : "Send"}
          placement="right-start"
        >
          <IconButton
            onClick={isResponding ? onStop : submit}
            disabled={isResponding ? false : !value.trim() || loading}
            aria-label={isResponding ? "Stop response" : "Send message"}
            sx={{
              ml: 1,
              width: 34,
              height: 34,
              color: "white",
              bgcolor: isResponding
                ? "rgba(239, 68, 68, 0.9)"
                : "rgba(255,255,255,0.14)",
              border: "1px solid rgba(255,255,255,0.2)",
              transition: "all 180ms ease",
              "&:hover": {
                bgcolor: isResponding
                  ? "rgba(239, 68, 68, 1)"
                  : "rgba(255,255,255,0.22)",
              },
              "&.Mui-disabled": {
                color: "rgba(255,255,255,0.35)",
                bgcolor: "rgba(255,255,255,0.08)",
                borderColor: "rgba(255,255,255,0.12)",
              },
            }}
          >
            <Fade
              in={isResponding}
              timeout={{ enter: 120, exit: 120 }}
              mountOnEnter
              unmountOnExit
            >
              <StopRoundedIcon sx={{ fontSize: 22 }} />
            </Fade>
            <Fade
              in={!isResponding}
              timeout={{ enter: 120, exit: 120 }}
              mountOnEnter
              unmountOnExit
            >
              <SendIcon fontSize="small" />
            </Fade>
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}